"use client";

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import TransparencySubNav from "@/components/TransparencySubNav";
import WikiStateGrid from "@/components/WikiStateGrid";
import Breadcrumbs from "@/components/Breadcrumbs";
import Footer from "@/components/Footer";
import InteractiveUSMap from "@/components/InteractiveUSMap";
// Figma map integration - uncomment when mapping is complete
import InteractiveMap from "@/components/figma-map/InteractiveMap";
import { MappingHelper } from "@/components/figma-map/MappingHelper";
import { StateModal } from "@/components/figma-map/StateModal";

interface StateData {
  name: string;
  code: string;
  responseTime: string;
  exemptions: number;
  statuteName?: string;
  statuteCitation?: string;
  population?: number;
  capitalCity?: string;
  officialWebsite?: string;
}

// Fallback data in case API is unavailable
const fallbackStateData: Record<string, StateData> = {
  AL: {
    name: "Alabama",
    code: "AL",
    responseTime: "5-7 business days",
    exemptions: 8,
    successRate: 72,
  },
  AK: {
    name: "Alaska",
    code: "AK",
    responseTime: "7-10 business days",
    exemptions: 9,
    successRate: 68,
  },
  AZ: {
    name: "Arizona",
    code: "AZ",
    responseTime: "5 business days",
    exemptions: 7,
    successRate: 76,
  },
  AR: {
    name: "Arkansas",
    code: "AR",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 71,
  },
  CA: {
    name: "California",
    code: "CA",
    responseTime: "14 calendar days",
    exemptions: 12,
    successRate: 81,
  },
  CO: {
    name: "Colorado",
    code: "CO",
    responseTime: "3 business days",
    exemptions: 7,
    successRate: 79,
  },
  CT: {
    name: "Connecticut",
    code: "CT",
    responseTime: "4 business days",
    exemptions: 9,
    successRate: 75,
  },
  DE: {
    name: "Delaware",
    code: "DE",
    responseTime: "15 business days",
    exemptions: 8,
    successRate: 70,
  },
  FL: {
    name: "Florida",
    code: "FL",
    responseTime: "3 business days",
    exemptions: 6,
    successRate: 82,
  },
  GA: {
    name: "Georgia",
    code: "GA",
    responseTime: "7 business days",
    exemptions: 9,
    successRate: 73,
  },
  HI: {
    name: "Hawaii",
    code: "HI",
    responseTime: "10 business days",
    exemptions: 8,
    successRate: 69,
  },
  ID: {
    name: "Idaho",
    code: "ID",
    responseTime: "5 business days",
    exemptions: 9,
    successRate: 71,
  },
  IL: {
    name: "Illinois",
    code: "IL",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 77,
  },
  IN: {
    name: "Indiana",
    code: "IN",
    responseTime: "15 days",
    exemptions: 9,
    successRate: 70,
  },
  IA: {
    name: "Iowa",
    code: "IA",
    responseTime: "10 business days",
    exemptions: 7,
    successRate: 74,
  },
  KS: {
    name: "Kansas",
    code: "KS",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 78,
  },
  KY: {
    name: "Kentucky",
    code: "KY",
    responseTime: "10 days",
    exemptions: 9,
    successRate: 69,
  },
  LA: {
    name: "Louisiana",
    code: "LA",
    responseTime: "3 business days",
    exemptions: 10,
    successRate: 66,
  },
  ME: {
    name: "Maine",
    code: "ME",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 75,
  },
  MD: {
    name: "Maryland",
    code: "MD",
    responseTime: "30 days",
    exemptions: 9,
    successRate: 68,
  },
  MA: {
    name: "Massachusetts",
    code: "MA",
    responseTime: "10 business days",
    exemptions: 9,
    successRate: 73,
  },
  MI: {
    name: "Michigan",
    code: "MI",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 76,
  },
  MN: {
    name: "Minnesota",
    code: "MN",
    responseTime: "5 business days",
    exemptions: 9,
    successRate: 79,
  },
  MS: {
    name: "Mississippi",
    code: "MS",
    responseTime: "1 business day",
    exemptions: 10,
    successRate: 64,
  },
  MO: {
    name: "Missouri",
    code: "MO",
    responseTime: "3 business days",
    exemptions: 9,
    successRate: 77,
  },
  MT: {
    name: "Montana",
    code: "MT",
    responseTime: "2 business days",
    exemptions: 8,
    successRate: 80,
  },
  NE: {
    name: "Nebraska",
    code: "NE",
    responseTime: "4 business days",
    exemptions: 8,
    successRate: 75,
  },
  NV: {
    name: "Nevada",
    code: "NV",
    responseTime: "15 days",
    exemptions: 7,
    successRate: 72,
  },
  NH: {
    name: "New Hampshire",
    code: "NH",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 76,
  },
  NJ: {
    name: "New Jersey",
    code: "NJ",
    responseTime: "7 business days",
    exemptions: 9,
    successRate: 71,
  },
  NM: {
    name: "New Mexico",
    code: "NM",
    responseTime: "15 days",
    exemptions: 9,
    successRate: 68,
  },
  NY: {
    name: "New York",
    code: "NY",
    responseTime: "20 business days",
    exemptions: 9,
    successRate: 70,
  },
  NC: {
    name: "North Carolina",
    code: "NC",
    responseTime: "5 business days",
    exemptions: 9,
    successRate: 74,
  },
  ND: {
    name: "North Dakota",
    code: "ND",
    responseTime: "7 business days",
    exemptions: 8,
    successRate: 72,
  },
  OH: {
    name: "Ohio",
    code: "OH",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 78,
  },
  OK: {
    name: "Oklahoma",
    code: "OK",
    responseTime: "5 business days",
    exemptions: 9,
    successRate: 70,
  },
  OR: {
    name: "Oregon",
    code: "OR",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 77,
  },
  PA: {
    name: "Pennsylvania",
    code: "PA",
    responseTime: "15 business days",
    exemptions: 9,
    successRate: 69,
  },
  RI: {
    name: "Rhode Island",
    code: "RI",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 76,
  },
  SC: {
    name: "South Carolina",
    code: "SC",
    responseTime: "3 business days",
    exemptions: 9,
    successRate: 72,
  },
  SD: {
    name: "South Dakota",
    code: "SD",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 75,
  },
  TN: {
    name: "Tennessee",
    code: "TN",
    responseTime: "10 business days",
    exemptions: 9,
    successRate: 70,
  },
  TX: {
    name: "Texas",
    code: "TX",
    responseTime: "10 business days",
    exemptions: 10,
    successRate: 65,
  },
  UT: {
    name: "Utah",
    code: "UT",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 79,
  },
  VT: {
    name: "Vermont",
    code: "VT",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 77,
  },
  VA: {
    name: "Virginia",
    code: "VA",
    responseTime: "5 business days",
    exemptions: 9,
    successRate: 73,
  },
  WA: {
    name: "Washington",
    code: "WA",
    responseTime: "5 business days",
    exemptions: 8,
    successRate: 78,
  },
  WV: {
    name: "West Virginia",
    code: "WV",
    responseTime: "5 business days",
    exemptions: 9,
    successRate: 71,
  },
  WI: {
    name: "Wisconsin",
    code: "WI",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 80,
  },
  WY: {
    name: "Wyoming",
    code: "WY",
    responseTime: "3 business days",
    exemptions: 8,
    successRate: 74,
  },
  DC: {
    name: "District of Columbia",
    code: "DC",
    responseTime: "15 business days",
    exemptions: 10,
    successRate: 70,
    statuteName: "D.C. Freedom of Information Act",
    statuteCitation: "D.C. Code § 2-531 et seq.",
    capitalCity: "Washington",
    population: 700000,
    officialWebsite: "https://dc.gov",
  },
  FEDERAL: {
    name: "Federal Government",
    code: "FEDERAL",
    responseTime: "20 business days",
    exemptions: 9,
    successRate: 65,
    statuteName: "Freedom of Information Act",
    statuteCitation: "5 U.S.C. § 552",
    capitalCity: "Washington, D.C.",
    population: 331900000,
    officialWebsite: "https://www.foia.gov",
  },
};

export default function TransparencyMap() {
  const [selectedState, setSelectedState] = useState<StateData | null>(null);
  const [hoveredState, setHoveredState] = useState<string | null>(null);
  const [stateData, setStateData] = useState<Record<string, StateData>>(fallbackStateData);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // Fetch jurisdiction data from Neon database on component mount
  useEffect(() => {
    const fetchJurisdictions = async () => {
      try {
        const response = await fetch('/api/transparency/jurisdictions');

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        setStateData(data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch jurisdiction data from Neon:', err);
        setError('Using cached data - database connection unavailable');
        // Fallback data is already set
      } finally {
        setLoading(false);
      }
    };

    fetchJurisdictions();
  }, []);

  const handleMapBackgroundClick = () => {
    navigate("/wiki/federal/foia");
  };

  return (
    <div className="min-h-screen">
      <Navigation />

      <div className="w-full bg-brand-martinique">
        <div className="max-w-[1440px] mx-auto px-8 pt-4">
          <Breadcrumbs items={[{ label: "Transparency Map" }]} variant="dark" />
        </div>

        <section className="w-full bg-brand-martinique">
          <div className="max-w-[1440px] mx-auto px-8 py-8 lg:py-12">
            <div className="mb-8">
              <div className="bg-white border border-brand-stroke rounded-lg p-4 lg:p-8 max-w-3xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="font-bold text-xl text-brand-dark">
                    FOIA jurisdictions
                  </h2>
                  {loading && (
                    <span className="text-sm text-brand-gray animate-pulse">
                      Loading from Neon database...
                    </span>
                  )}
                  {error && (
                    <span className="text-xs text-brand-orange">
                      {error}
                    </span>
                  )}
                  {!loading && !error && (
                    <span className="text-xs text-green-600 font-semibold">
                      ✓ Live data from Neon
                    </span>
                  )}
                </div>

                {/* Beautiful Figma map with spring animations */}
                <InteractiveMap
                  onStateClick={(stateCode) => {
                    const state = stateData[stateCode];
                    if (state) {
                      setSelectedState(state);
                    } else if (stateCode === "FEDERAL") {
                      // Show Federal FOIA details in modal
                      setSelectedState({
                        name: "Federal Government",
                        code: "FEDERAL",
                        responseTime: "20 business days",
                        exemptions: 9,
                        statuteName: "Freedom of Information Act",
                        statuteCitation: "5 U.S.C. § 552",
                        population: 331900000,
                        capitalCity: "Washington, D.C.",
                        officialWebsite: "https://www.foia.gov",
                      });
                    }
                  }}
                />
              </div>
            </div>

            <div className="mt-12 grid lg:grid-cols-2 gap-8">
              <div className="bg-brand-dark rounded-lg p-6 space-y-4">
                <h3 className="font-kepler text-xl font-bold text-white">
                  How to Use
                </h3>
                <ul className="space-y-3 text-sm text-brand-gray font-kepler">
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">1.</span>
                    <span>Hover over states to preview information</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">2.</span>
                    <span>Click to see detailed transparency laws</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">3.</span>
                    <span>Use the FOIA Generator with your state's rules</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">4.</span>
                    <span>Submit your optimized request</span>
                  </li>
                </ul>
              </div>

              <div className="bg-brand-dark rounded-lg p-6 space-y-4">
                <h3 className="font-kepler text-xl font-bold text-white">
                  What You'll Learn
                </h3>
                <ul className="space-y-3 text-sm text-brand-gray font-kepler">
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">📋</span>
                    <span>Response time requirements</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">⚖��</span>
                    <span>Applicable exemptions</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">✓</span>
                    <span>Success rate statistics</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-orange font-bold">📞</span>
                    <span>Agency contact information</span>
                  </li>
                </ul>
              </div>

              {/* State Grid for Quick Navigation */}
              <div className="mt-8">
                <h3 className="font-kepler text-xl font-bold text-white mb-4">
                  Jump to Any State
                </h3>
                <WikiStateGrid variant="dark" />
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>

      {/* State Details Modal Overlay */}
      <StateModal
        state={selectedState}
        onClose={() => setSelectedState(null)}
      />
    </div>
  );
}
